import { SENTRY_SUPPRESSION_REASONS, isSentrySuppressedReason, isSuppressibleAuthMessage, isSuppressibleNetworkMessage } from "./error.js";
//#region src/sentry.ts
const NON_NETWORK_REASONS = [
	"no_refresh_token",
	"refresh_expired",
	"refresh_revoked"
];
const SUPPRESSED_EXCEPTION_TYPES = /* @__PURE__ */ new Set(["DiskFullError"]);
function getNestedExtra(extra, field) {
	if (!extra) return void 0;
	if (field in extra) return extra[field];
	const metadata = extra.metadata;
	if (metadata && typeof metadata === "object" && field in metadata) return metadata[field];
}
const NON_ERROR_MESSAGE_LEVELS = /* @__PURE__ */ new Set([
	"warning",
	"info",
	"log",
	"debug"
]);
const SENTRY_PRODUCT_SIGNAL_TAG = "product_signal";
function isProductSignalEvent(event) {
	const tagValue = event.tags?.[SENTRY_PRODUCT_SIGNAL_TAG];
	return tagValue === true || tagValue === "true";
}
function captureProductSignal(sentry, message, options = {}) {
	return sentry.captureMessage(message, {
		level: options.level ?? "info",
		tags: {
			[SENTRY_PRODUCT_SIGNAL_TAG]: "true",
			...options.tags
		},
		extra: options.extra
	});
}
function shouldSuppressNonErrorMessage(event) {
	if (event.exception?.values?.length) return false;
	if (isProductSignalEvent(event)) return false;
	const level = (event.level ?? "info").toLowerCase();
	return NON_ERROR_MESSAGE_LEVELS.has(level);
}
function createNonErrorMessageFilter() {
	return (event) => shouldSuppressNonErrorMessage(event) ? null : event;
}
function composeSentryEventFilters(...filters) {
	return (event, hint) => {
		let current = event;
		for (const filter of filters) {
			if (!current) return null;
			current = filter(current, hint);
		}
		return current;
	};
}
function isAuthShaped(state, text) {
	if (!state.isAuthMessage || !text) return false;
	return state.isAuthMessage(text.toLowerCase());
}
function collectExceptionCandidateText(event, ex) {
	const parts = [];
	if (ex.value) parts.push(ex.value);
	if (event.message) parts.push(event.message);
	for (const key of ["errorMessage", "error"]) {
		const v = getNestedExtra(event.extra, key);
		if (typeof v === "string") parts.push(v);
	}
	return parts.join("\n");
}
function networkExceptionSuppressionResult(state, event, ex) {
	const exType = ex.type;
	if (!exType) return void 0;
	const lower = collectExceptionCandidateText(event, ex).toLowerCase();
	if (isAuthShaped(state, lower)) return false;
	if (isAuthShaped(state, event.message?.toLowerCase() || "")) return false;
	if (exType === "NetworkError" || SUPPRESSED_EXCEPTION_TYPES.has(exType)) return true;
	if (exType !== "TypeError" && exType !== "DOMException") {
		if (exType === "Error" && ex.value && isSuppressibleNetworkMessage(ex.value)) return true;
		return;
	}
	if (!state.isNetworkMessage) return;
	if (lower && state.isNetworkMessage(lower)) return true;
	const fragments = [
		ex.value,
		event.message,
		getNestedExtra(event.extra, "errorMessage"),
		getNestedExtra(event.extra, "error")
	];
	for (const fragment of fragments) {
		if (typeof fragment !== "string" || !fragment) continue;
		if (state.isNetworkMessage(fragment.toLowerCase())) return true;
	}
}
function isSuppressedByReason(event) {
	const reason = getNestedExtra(event.extra, "reason");
	return isSentrySuppressedReason(reason);
}
function isNetworkSentryEventForState(state, event) {
	const reason = getNestedExtra(event.extra, "reason");
	if (typeof reason === "string" && NON_NETWORK_REASONS.includes(reason)) return false;
	if (isSuppressedByReason(event)) return true;
	if (getNestedExtra(event.extra, "errorType") === "NetworkError") return true;
	const exceptionValues = event.exception?.values;
	if (exceptionValues) for (const ex of exceptionValues) {
		const fromException = networkExceptionSuppressionResult(state, event, ex);
		if (fromException !== void 0) return fromException;
	}
	if (state.isNetworkMessage && event.message) {
		const message = event.message.toLowerCase();
		if (state.isNetworkMessage(message)) {
			if (isAuthShaped(state, message)) return false;
			return true;
		}
	}
	return false;
}
function isNetworkSentryLogForState(state, log) {
	const reason = getNestedExtra(log.extra, "reason");
	if (typeof reason === "string" && NON_NETWORK_REASONS.includes(reason)) return false;
	if (isSuppressedByReason(log)) return true;
	if (getNestedExtra(log.extra, "errorType") === "NetworkError") return true;
	if (!state.isNetworkMessage) return false;
	const message = log.message?.toLowerCase() || "";
	if (state.isNetworkMessage(message)) {
		if (isAuthShaped(state, message)) return false;
		return true;
	}
	return false;
}
function createNetworkErrorSuppression(options) {
	const state = {
		isNetworkMessage: options?.isNetworkMessage,
		isAuthMessage: options?.isAuthMessage
	};
	return {
		isNetworkSentryEvent: (event) => isNetworkSentryEventForState(state, event),
		isNetworkSentryLog: (log) => isNetworkSentryLogForState(state, log),
		createNetworkSuppressionFilter: () => {
			return (event, _hint) => isNetworkSentryEventForState(state, event) ? null : event;
		},
		createNetworkLogSuppressionFilter: () => {
			return (log, _hint) => isNetworkSentryLogForState(state, log) ? null : log;
		}
	};
}
const defaultSuppression = createNetworkErrorSuppression({
	isNetworkMessage: isSuppressibleNetworkMessage,
	isAuthMessage: isSuppressibleAuthMessage
});
const isNetworkSentryEvent = defaultSuppression.isNetworkSentryEvent;
const isNetworkSentryLog = defaultSuppression.isNetworkSentryLog;
const createNetworkSuppressionFilter = defaultSuppression.createNetworkSuppressionFilter;
const createNetworkLogSuppressionFilter = defaultSuppression.createNetworkLogSuppressionFilter;
const RATE_LIMIT_EXCEPTION_TYPES = /* @__PURE__ */ new Set(["RateLimitError"]);
function eventHasExceptionType(event, types) {
	const values = event.exception?.values;
	if (!values) return false;
	return values.some((ex) => ex.type !== void 0 && types.has(ex.type));
}
function createRateLimitSuppressionFilter() {
	return (event) => {
		if (eventHasExceptionType(event, RATE_LIMIT_EXCEPTION_TYPES)) return null;
		if (getNestedExtra(event.extra, "reason") === SENTRY_SUPPRESSION_REASONS.rateLimit) return null;
		return event;
	};
}
function createDefaultSentryBeforeSend() {
	const filters = [];
	filters.push(createNetworkSuppressionFilter());
	filters.push(createRateLimitSuppressionFilter());
	filters.push(createNonErrorMessageFilter());
	return composeSentryEventFilters(...filters);
}
//#endregion
export { SENTRY_SUPPRESSION_REASONS, captureProductSignal, composeSentryEventFilters, createDefaultSentryBeforeSend, createNetworkErrorSuppression, createNetworkLogSuppressionFilter, createNetworkSuppressionFilter, createNonErrorMessageFilter, createRateLimitSuppressionFilter, isNetworkSentryEvent, isNetworkSentryLog, isProductSignalEvent, shouldSuppressNonErrorMessage };

//# sourceMappingURL=sentry.js.map